import React from 'react';

/**
 * Landing hero visual — currently a matched pair of therapist portraits.
 *
 * Iter 128 — extracted into its own component so the Q3 2026 hero-video
 * swap becomes a 1-line change: flip `REACT_APP_HERO_VIDEO_URL` in the
 * frontend env and the component switches to a silent, auto-playing loop.
 * The portrait branch remains the default (and fallback if the video is
 * blocked by data-saver / reduced-motion preferences).
 *
 * The video is intentionally muted + autoplay + playsInline so it works
 * on mobile without triggering audio prompts, and it's marked
 * `aria-hidden` because it's decorative.
 */
export default function HeroPortrait() {
  const videoUrl = process.env.REACT_APP_HERO_VIDEO_URL;
  const prefersReducedMotion = typeof window !== 'undefined'
    && window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (videoUrl && !prefersReducedMotion) {
    return (
      <div
        className="lg:col-span-5 relative self-end -mb-12 md:-mb-16 lg:-mb-20 mt-8 lg:mt-0"
        data-testid="hero-portrait-wrap"
      >
        <div className="relative w-full h-[300px] sm:h-[380px] md:h-[460px] lg:h-[560px] xl:h-[640px] overflow-hidden rounded-3xl">
          <video
            src={videoUrl}
            autoPlay
            loop
            muted
            playsInline
            aria-hidden="true"
            data-testid="hero-video"
            className="absolute inset-0 w-full h-full object-cover"
          />
          {/* Amber halo — kept for visual continuity with the portrait branch. */}
          <div
            className="absolute -bottom-6 left-1/2 -translate-x-1/2 w-[80%] h-24 rounded-[50%] pointer-events-none"
            style={{ background: 'rgba(255, 176, 0, 0.25)', filter: 'blur(48px)' }}
          />
        </div>
      </div>
    );
  }

  // Portrait branch — default. Iter 113e — photo cut-off aligned with hero
  // bottom line (no bleed below). Both portraits matched in height +
  // position, halo cleaned, visible on mobile.
  return (
    <div
      className="lg:col-span-5 relative self-end -mb-12 md:-mb-16 lg:-mb-20 mt-8 lg:mt-0"
      data-testid="hero-portrait-wrap"
    >
      <div className="relative w-full h-[300px] sm:h-[380px] md:h-[460px] lg:h-[560px] xl:h-[640px] overflow-visible">
        {/* Female — STATIC, arms crossed, no tablet (iter 113l) */}
        <img
          src="/therapist-portrait-female.png"
          alt="Female physiotherapist with arms crossed"
          data-testid="hero-portrait-female"
          className="absolute bottom-0 md:-bottom-2 lg:-bottom-6 -left-4 sm:-left-6 md:-left-8 lg:-left-10 xl:-left-12 h-full w-auto object-contain z-20 drop-shadow-[0_25px_45px_rgba(0,0,0,0.40)]"
          loading="eager"
        />
        {/* Male — same height, positioned to the right of female with slight
            bleed. Brought slightly higher than the female to match its new
            position. */}
        <img
          src="/therapist-portrait-male.png"
          alt="Male physiotherapist with arms crossed"
          data-testid="hero-portrait-male"
          className="absolute bottom-0 md:-bottom-2 lg:-bottom-6 -right-12 sm:-right-16 md:-right-20 lg:-right-32 xl:-right-44 h-full w-auto object-contain z-10 drop-shadow-[0_25px_45px_rgba(0,0,0,0.40)]"
          loading="eager"
        />
      </div>
    </div>
  );
}
